/**
 * Onboarding funnel analytics — fires PostHog events at the key
 * transitions so the team can measure where users drop off.
 *
 * Implements the funnel events listed in task 1.12 of the launch plan
 * (`docs/moon-launch-plan/EXECUTION_PLAN.md`). Each event carries the
 * step name plus whatever persona dimensions are known at fire time.
 *
 * Events:
 *   - onboarding_started — first view of WelcomeGoals
 *   - onboarding_step_viewed — every screen focus
 *   - onboarding_step_submitted — when user advances forward (tracked by
 *     consumers; the navigator focus event covers most cases)
 *   - onboarding_completed — account created + data saved
 *   - onboarding_abandoned — Skip-for-now (or other early bail)
 *
 * Usage from a hook context:
 *
 *   const trackOnboarding = useOnboardingAnalytics();
 *   trackOnboarding.stepViewed("WelcomeGoals");
 *
 * The hook returns no-op functions when PostHog isn't available (e.g.,
 * tests or initial boot before init), so callers can fire freely.
 *
 * The five events are the shared flow shape, so this is a thin persona-aware
 * wrapper over `useFlowAnalytics("onboarding")` rather than its own capture
 * plumbing. Event names and payloads are unchanged.
 */
import { useMemo } from "react";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import type { OnboardingData } from "./types";

type StepName =
  | "WelcomeGoals"
  | "FamilyBuildingPath"
  | "JourneyStage"
  | "TreatmentType"
  | "HowAreYouHelping"
  | "WorkingWithSurrogate"
  | "Sex"
  | "Birthday"
  | "Location"
  | "CreateAccount"
  | "PartnerJourneyConfirm";

/**
 * Persona dimensions captured so far in the onboarding flow.
 * Sent with every event so funnels can be sliced by persona.
 */
function personaProps(data: Partial<OnboardingData>) {
  return {
    primary_intent: data.primaryIntent ?? null,
    treatment_intent: data.treatmentIntent ?? null,
    fertility_stage: data.fertilityStage ?? null,
    planned_treatment_modality: data.plannedTreatmentModality ?? null,
    current_treatment_family: data.currentTreatmentFamily ?? null,
    gender: data.gender ?? null,
    journey_role: data.journeyRole ?? null,
    partner_choice: data.partnerChoice ?? null,
  };
}

export function useOnboardingAnalytics() {
  const flow = useFlowAnalytics("onboarding");

  return useMemo(
    () => ({
      started: (data: Partial<OnboardingData>) =>
        flow.started(personaProps(data)),
      stepViewed: (step: StepName, data: Partial<OnboardingData>) =>
        flow.stepViewed(step, personaProps(data)),
      stepSubmitted: (step: StepName, data: Partial<OnboardingData>) =>
        flow.stepSubmitted(step, personaProps(data)),
      completed: (data: Partial<OnboardingData>) =>
        flow.completed(personaProps(data)),
      abandoned: (
        reason:
          | "skip-for-now"
          | "metadata-retry-failed"
          | "patient-save-failed"
          | "partner-accept-failed"
          | "partner-accept-skipped"
          | "other",
        data: Partial<OnboardingData>,
      ) => flow.abandoned(reason, personaProps(data)),
    }),
    [flow],
  );
}
